import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dirForLang, normalizeLang } from "../i18n";
import { useAuth } from "../auth/AuthContext";
import JourneyService from "../services/JourneyService";
import { getSystemStrings } from "../strings/systemStrings";
import LegalFooterLinks from "../components/LegalFooterLinks";
import "./DisclaimerGate.css";

function useQueryParams() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const lang = normalizeLang(params.get("lang") || "he");
  const problemKey = params.get("problemKey") || "rel_breakup_fresh_pain";
  return { lang, problemKey, params };
}

function disclaimerKey(lang, problemKey) {
  // Acceptance should follow the journey/problem, not the UI language.
  // This prevents forcing the user to re-accept when switching languages.
  return `disclaimerAccepted:${problemKey}`;
}

export default function DisclaimerGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang, problemKey } = useQueryParams();
  const [checked, setChecked] = useState(false);
  const content = getSystemStrings(lang, user?.gender).disclaimer;
  const overviewTarget = JourneyService.getOverviewTarget(lang, problemKey);

  const accepted = useMemo(
    () => localStorage.getItem(disclaimerKey(lang, problemKey)) === "true",
    [problemKey]
  );

  useEffect(() => {
    if (!accepted) return;
    navigate(overviewTarget, { replace: true });
  }, [accepted, navigate, overviewTarget]);

  if (accepted) return null;

  return (
    <div className="disclaimer-gate" dir={dirForLang(lang)}>
      <div className="disclaimer-card">
        <h1>{content.title}</h1>
        {content.body.map((line, idx) => (
          <p key={`line-${idx}`}>{line}</p>
        ))}
        <label className="disclaimer-check">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>{content.checkboxLabel}</span>
        </label>
        <button
          type="button"
          className="disclaimer-primary"
          disabled={!checked}
          onClick={() => {
            localStorage.setItem(disclaimerKey(lang, problemKey), "true");
            navigate(overviewTarget, { replace: true });
          }}
        >
          {content.cta}
        </button>
        <LegalFooterLinks className="disclaimer-legal-links" />
      </div>
    </div>
  );
}
